import { useQuery } from "@tanstack/react-query";
import { RequestStatusRenderer } from "components/requestStatsRenderer/RequestStatusRenderer";
import {
  createDateRange,
  noData,
  validBarData,
  validLineData,
  type PerformanceWithAccountFilter,
} from "features/Library/Common/utils/performanceUtils";
import {
  customerEnrolledStatisticsColorRange,
  getTemplateCustomerEnrolledLineChartQuery,
  getTemplateCustomerEnrolledStatisticsQuery,
} from "./queries";
import { ChartWrapper, ColumnChart, getDateFormatByCount, HorizontalBarChart, LineChart } from "components/charts";
import CardReporting, { type CardReportingItem } from "components/cards/CardReporting/CardReporting";

import "features/Library/Common/utils/performanceSCSSUtils.scss";
import {
  sharedAccountReportingHorizontalBarProps,
  sharedAccountReportingLineProps,
} from "features/Reporting/Content/shared";
import { type ReactNode, useMemo, useState } from "react";
import { sharedGoogleReportingBarProps } from "features/Reporting/Google/utils/shared";

import styles from "./customersSection.module.scss";
import { type DataPoint } from "components/charts/types/HorizontalBarChart";
import { CHART_PERIOD, useChartPeriodMeasure } from "hooks/useChartPeriodMeasure";
import cn from "classnames";

export interface Props {
  templateId: number;
  filter: PerformanceWithAccountFilter;
}

export const CustomersSection = ({ templateId, filter }: Props) => {
  const [chartState, setChartState] = useState<"column" | "line">("line");

  const dateRange = useMemo(() => createDateRange(filter.dateFrom, filter.dateTo), [filter.dateFrom, filter.dateTo]);
  const [chartPeriod, measureRef] = useChartPeriodMeasure(
    dateRange,
    sharedGoogleReportingBarProps.margins!.left + sharedGoogleReportingBarProps.margins!.right,
    chartState === "column",
  );
  const lineChartFilter = useMemo(() => ({ ...filter, aggregation: chartPeriod }), [filter, chartPeriod]);
  const lineChartQuery = useQuery({
    queryKey: ["templateCustomerEnrolledLineChart", templateId, lineChartFilter],
    queryFn: getTemplateCustomerEnrolledLineChartQuery,
    enabled: lineChartFilter.aggregation !== CHART_PERIOD.UNSET && lineChartFilter.type !== undefined,
  });

  const engagementQuery = useQuery({
    queryKey: ["templateCustomerEngagement", templateId, filter],
    queryFn: getTemplateCustomerEnrolledStatisticsQuery,
    enabled: filter.type !== undefined,
  });

  const customerLineChart = useMemo<ReactNode>(() => {
    if (!lineChartQuery.data || !validLineData(lineChartQuery.data.lineData.yData)) {
      return noData(filter);
    }
    return (
      <LineChart
        {...sharedAccountReportingLineProps}
        xData={lineChartQuery.data.lineData.xData}
        yData={lineChartQuery.data.lineData.yData}
        xFormatterFunc={getDateFormatByCount(lineChartQuery.data.lineData.xData.length)}
      />
    );
  }, [filter, lineChartQuery.data]);

  const customerBarChart = useMemo<ReactNode>(() => {
    if (!lineChartQuery.data || !validBarData(lineChartQuery.data.barData.data)) {
      return noData(filter);
    }
    const { tooltipFormatter: _ignore, ...googleBarProps } = sharedGoogleReportingBarProps;

    return (
      <ColumnChart
        {...googleBarProps}
        data={lineChartQuery.data.barData.data}
        domain={lineChartQuery.data.barData.domain}
        xAxisTickFormat={lineChartQuery.data.barData.xAxisTickFormat}
        onClick={() => {}}
      />
    );
  }, [filter, lineChartQuery.data]);

  const handleChartTypeChange = (option: string) => {
    setChartState(option.toLowerCase().includes("line") ? "line" : "column");
  };

  const barData = useMemo<DataPoint[]>(() => {
    if (!engagementQuery.data) {
      return [];
    }
    return [
      {
        id: "Invited",
        value: engagementQuery.data.Invites,
        category: "Invited",
        fillColor: customerEnrolledStatisticsColorRange[0],
      },
      {
        id: "Accepted",
        value: engagementQuery.data.Enrollments,
        category: "Accepted",
        fillColor: customerEnrolledStatisticsColorRange[1],
      },
      {
        id: "Declined",
        value: engagementQuery.data.Declines,
        category: "Declined",
        fillColor: customerEnrolledStatisticsColorRange[2],
      },
    ];
  }, [engagementQuery.data]);

  const cards = useMemo<CardReportingItem[]>(() => {
    if (!engagementQuery.data) {
      return [];
    }
    return [
      {
        statDescription: "Invited",
        stat: engagementQuery.data.Invites,
        popupBody: "Total customers invited to join this template during the specified time period.",
      },
      {
        statDescription: "Enrolled/Accepted",
        stat: engagementQuery.data.Enrollments,
        popupBody:
          "Total customers who enrolled in the template/accepted the invitation during the specified time period.",
      },
      {
        statDescription: "Declined",
        stat: engagementQuery.data.Declines,
        popupBody: "Total customers who declined the template invitation during the specified time period.",
      },
      {
        statDescription: "Enrollment Rate",
        stat: engagementQuery.data.EnrollmentRate * 100,
        isStatPercentage: true,
        popupBody: "Percentage of invited customers who enrolled in the template during the specified time period.",
      },
    ];
  }, [engagementQuery.data]);

  return (
    <div className={styles.root}>
      <h2 className={styles.summaryHeader}>Customers</h2>
      <div className="graphs">
        <div ref={measureRef} className={cn(styles.lineChart, "lineChartContainer")}>
          <ChartWrapper
            showLegend
            legendLabels={["Total Customers"]}
            titles={["Enrollment Activity - Line Chart", "Enrollment Activity - Bar Chart"]}
            onChange={handleChartTypeChange}
          >
            <RequestStatusRenderer state={lineChartQuery.status}>
              {chartState === "column" ? customerBarChart : customerLineChart}
            </RequestStatusRenderer>
          </ChartWrapper>
        </div>
        <div className="funnelChartContainer">
          <ChartWrapper
            showLegend
            legendLabels={["Invited", "Accepted", "Declined"]}
            titles={["Engagement"]}
            colorRange={customerEnrolledStatisticsColorRange}
          >
            <RequestStatusRenderer state={engagementQuery.status}>
              {engagementQuery.data && validBarData(barData) ? (
                <HorizontalBarChart {...sharedAccountReportingHorizontalBarProps} data={barData} />
              ) : (
                noData(filter)
              )}
            </RequestStatusRenderer>
          </ChartWrapper>
        </div>
      </div>
      <div className={styles.cards}>
        <RequestStatusRenderer state={engagementQuery.status}>
          {engagementQuery.data && <CardReporting items={cards} />}
        </RequestStatusRenderer>
      </div>
    </div>
  );
};
